import React, { useState, useEffect } from 'react';
import { 
  Filter, Plus, Search, X, Eye, Edit2, Trash2, Play, Pause, 
  Download, TrendingUp, Calendar, DollarSign, Target, BarChart3,
  CheckCircle, AlertCircle
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { campaignsAPI } from '../services/api';

// Campaign Detail Modal Component
function CampaignDetailModal({ campaign, onClose, onEdit }) {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (campaign) {
      loadMetrics();
    }
  }, [campaign]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await campaignsAPI.getMetrics(campaign.campaign_id, 30);
      setMetrics(response.data.metrics || []);
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!campaign) return null;

  // Prepare chart data
  const chartData = metrics.slice().reverse().map(m => ({
    date: new Date(m.metric_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    impressions: m.impressions,
    clicks: m.clicks,
    conversions: m.conversions,
    ctr: (m.ctr * 100).toFixed(2),
    cpa: m.cpa.toFixed(2)
  }));

  // Calculate summary stats
  const totalImpressions = metrics.reduce((sum, m) => sum + m.impressions, 0);
  const totalClicks = metrics.reduce((sum, m) => sum + m.clicks, 0);
  const totalConversions = metrics.reduce((sum, m) => sum + m.conversions, 0);
  const avgCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const avgCPA = totalConversions > 0 ? campaign.spend / totalConversions : 0;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="campaign-detail-modal"
      >
        {/* Header */}
        <div style={{ 
          padding: '24px', 
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start'
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '8px' }}>
              {campaign.name}
            </h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span className={`badge badge-${campaign.status === 'active' ? 'success' : 'info'}`}>
                {campaign.status}
              </span>
              <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
                {campaign.channel}
              </span>
              <span className="badge" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                {campaign.objective?.replace('_', ' ')}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => onEdit(campaign)} 
              className="btn btn-secondary"
              data-testid="edit-campaign-btn"
            >
              <Edit2 size={16} />
              Edit
            </button>
            <button onClick={onClose} className="btn btn-secondary">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '2px solid var(--border-light)', padding: '0 24px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['overview', 'metrics', 'performance'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: activeTab === tab ? '3px solid var(--primary-500)' : '3px solid transparent',
                  color: activeTab === tab ? 'var(--primary-600)' : 'var(--text-secondary)',
                  fontWeight: activeTab === tab ? '600' : '500',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  fontSize: '0.875rem'
                }}
                data-testid={`tab-${tab}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {activeTab === 'overview' && (
            <div>
              {/* Key Metrics */}
              <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Eye size={16} style={{ color: 'var(--primary-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Total Impressions</p>
                  </div>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                    {totalImpressions.toLocaleString()}
                  </p>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Target size={16} style={{ color: 'var(--accent-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Total Clicks</p>
                  </div>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                    {totalClicks.toLocaleString()}
                  </p>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <CheckCircle size={16} style={{ color: 'var(--success-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Conversions</p>
                  </div>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--success-600)' }}>
                    {totalConversions.toLocaleString()}
                  </p>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <DollarSign size={16} style={{ color: 'var(--warning-500)' }} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Avg CPA</p>
                  </div>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                    ${avgCPA.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Campaign Details */}
              <div className="grid grid-cols-2" style={{ gap: '24px' }}>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>Campaign Details</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Campaign ID</span>
                      <span style={{ fontWeight: '600', fontFamily: 'monospace', fontSize: '0.875rem' }}>{campaign.campaign_id}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Total Budget</span>
                      <span style={{ fontWeight: '700', fontSize: '1.125rem', color: 'var(--primary-600)' }}>
                        ${campaign.spend?.toLocaleString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Start Date</span>
                      <span style={{ fontWeight: '600' }}>
                        {new Date(campaign.start_date).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'long', day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>End Date</span>
                      <span style={{ fontWeight: '600' }}>
                        {new Date(campaign.end_date).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'long', day: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>Performance Scores</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Effectiveness Score */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Effectiveness Score</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--success-600)' }}>
                          {((campaign.effectiveness_score || 0.75) * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div style={{ height: '12px', background: 'var(--bg-tertiary)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${(campaign.effectiveness_score || 0.75) * 100}%`, 
                          height: '100%', 
                          background: 'linear-gradient(90deg, var(--success-500), var(--success-600))',
                          transition: 'width 0.5s ease'
                        }}></div>
                      </div>
                    </div>

                    {/* Efficiency Percentile */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Efficiency Percentile</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-600)' }}>
                          {((campaign.efficiency_percentile || 68))}th
                        </span>
                      </div>
                      <div style={{ height: '12px', background: 'var(--bg-tertiary)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${campaign.efficiency_percentile || 68}%`, 
                          height: '100%', 
                          background: 'linear-gradient(90deg, var(--accent-500), var(--accent-600))'
                        }}></div>
                      </div>
                    </div>

                    {/* Data Completeness */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>Data Completeness</span>
                        <span style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--primary-600)' }}>
                          {(campaign.data_completeness_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div style={{ height: '12px', background: 'var(--bg-tertiary)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${campaign.data_completeness_score * 100}%`, 
                          height: '100%', 
                          background: 'linear-gradient(90deg, var(--primary-500), var(--primary-600))'
                        }}></div>
                      </div>
                    </div>

                    {/* Average CTR */}
                    <div style={{ padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Average CTR</span>
                        <span style={{ fontWeight: '700' }}>{avgCTR.toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'metrics' && (
            <div>
              {loading ? (
                <div className="loading"><div className="spinner"></div></div>
              ) : chartData.length === 0 ? (
                <div className="empty-state">
                  <BarChart3 size={48} className="empty-state-icon" />
                  <p>No metrics data available</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Impressions & Clicks Chart */}
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>
                      Impressions & Clicks Trend
                    </h3>
                    <div style={{ height: '250px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                          <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                          <YAxis style={{ fontSize: '12px' }} />
                          <Tooltip />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="impressions" 
                            stroke="var(--primary-500)" 
                            strokeWidth={2}
                            name="Impressions"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="clicks" 
                            stroke="var(--accent-500)" 
                            strokeWidth={2}
                            name="Clicks"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Conversions Chart */}
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>
                      Conversions Over Time
                    </h3>
                    <div style={{ height: '250px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                          <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                          <YAxis style={{ fontSize: '12px' }} />
                          <Tooltip />
                          <Area 
                            type="monotone" 
                            dataKey="conversions" 
                            stroke="var(--success-500)" 
                            fill="hsl(142, 76%, 90%)"
                            name="Conversions"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* CTR & CPA Chart */}
                  <div className="grid grid-cols-2" style={{ gap: '24px' }}>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>CTR Trend</h3>
                      <div style={{ height: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                            <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                            <YAxis style={{ fontSize: '12px' }} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Line 
                              type="monotone" 
                              dataKey="ctr" 
                              stroke="var(--info-500)" 
                              strokeWidth={3}
                              dot={{ fill: 'var(--info-500)', r: 4 }}
                              name="CTR %"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>CPA Trend</h3>
                      <div style={{ height: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                            <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                            <YAxis style={{ fontSize: '12px' }} />
                            <Tooltip formatter={(value) => `$${value}`} />
                            <Line 
                              type="monotone" 
                              dataKey="cpa" 
                              stroke="var(--warning-500)" 
                              strokeWidth={3}
                              dot={{ fill: 'var(--warning-500)', r: 4 }}
                              name="CPA $"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'performance' && (
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>Performance Analysis</h3>
              
              {/* Performance Indicators */}
              <div className="grid grid-cols-3" style={{ gap: '16px', marginBottom: '24px' }}>
                <div style={{ 
                  padding: '16px', 
                  background: avgCTR > 2 ? 'hsl(142, 76%, 96%)' : 'hsl(38, 92%, 95%)',
                  borderRadius: '12px',
                  border: `2px solid ${avgCTR > 2 ? 'var(--success-500)' : 'var(--warning-500)'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {avgCTR > 2 ? <CheckCircle size={20} style={{ color: 'var(--success-600)' }} /> : <AlertCircle size={20} style={{ color: 'var(--warning-600)' }} />}
                    <p style={{ fontSize: '0.875rem', fontWeight: '600' }}>CTR Performance</p>
                  </div>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{avgCTR.toFixed(2)}%</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {avgCTR > 2 ? 'Above benchmark' : 'Below benchmark (2%)'}
                  </p>
                </div>

                <div style={{ 
                  padding: '16px', 
                  background: avgCPA < 50 ? 'hsl(142, 76%, 96%)' : 'hsl(38, 92%, 95%)',
                  borderRadius: '12px',
                  border: `2px solid ${avgCPA < 50 ? 'var(--success-500)' : 'var(--warning-500)'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {avgCPA < 50 ? <CheckCircle size={20} style={{ color: 'var(--success-600)' }} /> : <AlertCircle size={20} style={{ color: 'var(--warning-600)' }} />}
                    <p style={{ fontSize: '0.875rem', fontWeight: '600' }}>CPA Efficiency</p>
                  </div>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>${avgCPA.toFixed(2)}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {avgCPA < 50 ? 'Efficient' : 'Above target ($50)'}
                  </p>
                </div>

                <div style={{ 
                  padding: '16px', 
                  background: 'hsl(199, 89%, 96%)',
                  borderRadius: '12px',
                  border: '2px solid var(--info-500)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <TrendingUp size={20} style={{ color: 'var(--info-600)' }} />
                    <p style={{ fontSize: '0.875rem', fontWeight: '600' }}>ROI Estimate</p>
                  </div>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                    {((totalConversions * 100 - campaign.spend) / campaign.spend * 100).toFixed(0)}%
                  </p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Assuming $100 per conversion
                  </p>
                </div>
              </div>

              {/* Recommendations */}
              <div style={{ padding: '16px', background: 'hsl(180, 65%, 97%)', borderRadius: '12px', border: '1px solid var(--accent-500)' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--accent-700)', marginBottom: '12px' }}>
                  AI Recommendations for This Campaign
                </h4>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {avgCTR < 2 && <li style={{ fontSize: '0.875rem' }}>• Consider refreshing ad creative to improve CTR</li>}
                  {avgCPA > 50 && <li style={{ fontSize: '0.875rem' }}>• Optimize targeting to reduce cost per acquisition</li>}
                  {totalConversions < 100 && <li style={{ fontSize: '0.875rem' }}>• Review landing page conversion funnel</li>}
                  <li style={{ fontSize: '0.875rem' }}>• A/B test different messaging approaches</li>
                  <li style={{ fontSize: '0.875rem' }}>• Monitor competitor activity for market insights</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Create/Edit Campaign Modal
function CampaignFormModal({ campaign, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    objective: campaign?.objective || 'brand_awareness',
    channel: campaign?.channel || 'search',
    start_date: campaign?.start_date ? new Date(campaign.start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    end_date: campaign?.end_date ? new Date(campaign.end_date).toISOString().split('T')[0] : new Date(Date.now() + 90*24*60*60*1000).toISOString().split('T')[0],
    spend: campaign?.spend || 50000
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await campaignsAPI.create(formData);
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Error saving campaign. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          width: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '24px'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="campaign-form-modal"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>
            {campaign ? 'Edit Campaign' : 'Create New Campaign'}
          </h2>
          <button onClick={onClose} className="btn btn-secondary">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Campaign Name */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                Campaign Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Q1 Brand Awareness Campaign"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
                data-testid="campaign-name-input"
              />
            </div>

            {/* Objective & Channel */}
            <div className="grid grid-cols-2" style={{ gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Objective *
                </label>
                <select
                  value={formData.objective}
                  onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    background: 'white'
                  }}
                  data-testid="campaign-objective-select"
                >
                  <option value="brand_awareness">Brand Awareness</option>
                  <option value="lead_generation">Lead Generation</option>
                  <option value="sales">Sales</option>
                  <option value="engagement">Engagement</option>
                  <option value="conversion">Conversion</option>
                  <option value="product_launch">Product Launch</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Channel *
                </label>
                <select
                  value={formData.channel}
                  onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    background: 'white'
                  }}
                  data-testid="campaign-channel-select"
                >
                  <option value="search">Search</option>
                  <option value="social">Social</option>
                  <option value="display">Display</option>
                  <option value="video">Video</option>
                  <option value="email">Email</option>
                  <option value="native">Native</option>
                </select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2" style={{ gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem'
                  }}
                  data-testid="campaign-start-date"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem'
                  }}
                  data-testid="campaign-end-date"
                />
              </div>
            </div>

            {/* Budget */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                Total Budget ($) *
              </label>
              <input
                type="number"
                value={formData.spend}
                onChange={(e) => setFormData({ ...formData, spend: parseFloat(e.target.value) })}
                required
                min="1000"
                step="1000"
                placeholder="50000"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
                data-testid="campaign-budget-input"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                Recommended: $10,000 - $500,000
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={saving}
                style={{ flex: 1, justifyContent: 'center' }}
                data-testid="save-campaign-btn"
              >
                {saving ? 'Saving...' : (campaign ? 'Update Campaign' : 'Create Campaign')}
              </button>
              <button 
                type="button" 
                onClick={onClose} 
                className="btn btn-secondary"
                style={{ flex: 1, justifyContent: 'center' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Campaigns Page Component
function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    objective: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState({ field: 'start_date', order: 'desc' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.channel) params.channel = filters.channel;
        
        const response = await campaignsAPI.getAll(params);
        let data = response.data.campaigns || [];
        
        // Client-side filters
        if (filters.search) {
          data = data.filter(c => 
            c.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
            c.campaign_id?.toLowerCase().includes(filters.search.toLowerCase())
          );
        }
        
        if (filters.objective) {
          data = data.filter(c => c.objective === filters.objective);
        }

        // Sorting
        data.sort((a, b) => {
          const aVal = a[sortBy.field];
          const bVal = b[sortBy.field];
          if (sortBy.order === 'asc') {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });
        
        setCampaigns(data);
      } catch (error) {
        console.error('Error loading campaigns:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters, sortBy]);

  const handleViewDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setShowFormModal(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowFormModal(true);
    setShowDetailModal(false);
  };

  const handleFormSave = async () => {
    // Reload campaigns
    setFilters({ ...filters }); // Trigger reload
  };

  const toggleSelectCampaign = (campaignId) => {
    if (selectedCampaigns.includes(campaignId)) {
      setSelectedCampaigns(selectedCampaigns.filter(id => id !== campaignId));
    } else {
      setSelectedCampaigns([...selectedCampaigns, campaignId]);
    }
  };

  const selectAllCampaigns = () => {
    if (selectedCampaigns.length === campaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(campaigns.map(c => c.campaign_id));
    }
  };

  const getEfficiencyColor = (score) => {
    if (score >= 0.8) return 'var(--success-500)';
    if (score >= 0.6) return 'var(--warning-500)';
    return 'var(--error-500)';
  };

  const handleSort = (field) => {
    if (sortBy.field === field) {
      setSortBy({ field, order: sortBy.order === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortBy({ field, order: 'desc' });
    }
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Campaigns</h1>
          <p className="page-subtitle">Manage and analyze marketing campaigns</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" data-testid="export-campaigns-btn">
            <Download size={20} />
            Export
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleCreateCampaign}
            data-testid="create-campaign-btn"
          >
            <Plus size={20} />
            Create Campaign
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Search
            </label>
            <div style={{ position: 'relative' }}>
              <Search 
                size={18} 
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-tertiary)' 
                }} 
              />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 38px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
                data-testid="search-campaigns"
              />
            </div>
          </div>
          
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: 'white'
              }}
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Channel
            </label>
            <select
              value={filters.channel}
              onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: 'white'
              }}
              data-testid="filter-channel"
            >
              <option value="">All Channels</option>
              <option value="search">Search</option>
              <option value="social">Social</option>
              <option value="display">Display</option>
              <option value="video">Video</option>
              <option value="email">Email</option>
            </select>
          </div>

          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Objective
            </label>
            <select
              value={filters.objective}
              onChange={(e) => setFilters({ ...filters, objective: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: 'white'
              }}
              data-testid="filter-objective"
            >
              <option value="">All Objectives</option>
              <option value="brand_awareness">Brand Awareness</option>
              <option value="lead_generation">Lead Generation</option>
              <option value="sales">Sales</option>
              <option value="engagement">Engagement</option>
              <option value="conversion">Conversion</option>
            </select>
          </div>
        </div>

        {/* Active Filters & Stats */}
        {(filters.status || filters.channel || filters.objective || filters.search) && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Active filters:</span>
              {filters.status && <span className="badge badge-info">{filters.status}</span>}
              {filters.channel && <span className="badge badge-info">{filters.channel}</span>}
              {filters.objective && <span className="badge badge-info">{filters.objective.replace('_', ' ')}</span>}
              {filters.search && <span className="badge badge-info">"{filters.search}"</span>}
            </div>
            <button
              onClick={() => setFilters({ status: '', channel: '', objective: '', search: '' })}
              style={{
                padding: '6px 12px',
                border: 'none',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Campaigns</p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>{campaigns.length}</p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Spend</p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-600)' }}>
            ${campaigns.reduce((sum, c) => sum + (c.spend || 0), 0).toLocaleString()}
          </p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Active Campaigns</p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--success-600)' }}>
            {campaigns.filter(c => c.status === 'active').length}
          </p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Avg Effectiveness</p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--accent-600)' }}>
            {campaigns.length > 0 ? 
              ((campaigns.reduce((sum, c) => sum + (c.effectiveness_score || 0.75), 0) / campaigns.length) * 100).toFixed(0) 
              : 0}%
          </p>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedCampaigns.length > 0 && (
        <div style={{ 
          padding: '16px 24px', 
          background: 'var(--primary-500)', 
          color: 'white',
          borderRadius: '12px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontWeight: '600' }}>{selectedCampaigns.length} selected</span>
            <button
              onClick={() => setSelectedCampaigns([])}
              style={{
                padding: '4px 12px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Clear
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }}>
              <Pause size={16} />
              Pause Selected
            </button>
            <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }}>
              <Trash2 size={16} />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Campaigns Table */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Target size={64} className="empty-state-icon" />
            <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '8px' }}>No campaigns found</p>
            <p>Try adjusting your filters or create a new campaign</p>
            <button 
              onClick={handleCreateCampaign}
              className="btn btn-primary" 
              style={{ marginTop: '16px' }}
            >
              <Plus size={20} />
              Create Your First Campaign
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)', textAlign: 'left' }}>
                  <th style={{ padding: '16px 12px', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedCampaigns.length === campaigns.length && campaigns.length > 0}
                      onChange={selectAllCampaigns}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th 
                    style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    Campaign Name {sortBy.field === 'name' && (sortBy.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Objective</th>
                  <th style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Channel</th>
                  <th style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Status</th>
                  <th 
                    style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer' }}
                    onClick={() => handleSort('spend')}
                  >
                    Spend {sortBy.field === 'spend' && (sortBy.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer' }}
                    onClick={() => handleSort('start_date')}
                  >
                    Dates {sortBy.field === 'start_date' && (sortBy.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Effectiveness</th>
                  <th style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Data Quality</th>
                  <th style={{ padding: '16px 12px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '0.875rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign, idx) => (
                  <tr 
                    key={idx} 
                    style={{ 
                      borderBottom: '1px solid var(--border-light)',
                      transition: 'background 0.2s',
                      background: selectedCampaigns.includes(campaign.campaign_id) ? 'hsl(210, 100%, 98%)' : 'transparent'
                    }}
                    onMouseEnter={(e) => !selectedCampaigns.includes(campaign.campaign_id) && (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                    onMouseLeave={(e) => !selectedCampaigns.includes(campaign.campaign_id) && (e.currentTarget.style.background = 'transparent')}
                    data-testid={`campaign-row-${idx}`}
                  >
                    <td style={{ padding: '16px 12px' }}>
                      <input
                        type="checkbox"
                        checked={selectedCampaigns.includes(campaign.campaign_id)}
                        onChange={() => toggleSelectCampaign(campaign.campaign_id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td 
                      style={{ padding: '16px 12px', cursor: 'pointer' }}
                      onClick={() => handleViewDetails(campaign)}
                    >
                      <div>
                        <p style={{ fontWeight: '600', marginBottom: '4px', color: 'var(--primary-600)' }}>
                          {campaign.name}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                          {campaign.campaign_id}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', textTransform: 'capitalize', fontSize: '0.875rem' }}>
                      {campaign.objective?.replace('_', ' ')}
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <span className="badge badge-info" style={{ textTransform: 'capitalize' }}>
                        {campaign.channel}
                      </span>
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <span className={`badge badge-${campaign.status === 'active' ? 'success' : campaign.status === 'completed' ? 'info' : 'warning'}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px 12px', fontWeight: '600' }}>
                      ${(campaign.spend || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '16px 12px', fontSize: '0.875rem' }}>
                      <div>
                        <p>{new Date(campaign.start_date).toLocaleDateString()}</p>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                          to {new Date(campaign.end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '8px', 
                          background: 'var(--bg-tertiary)', 
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            width: `${(campaign.effectiveness_score || 0.75) * 100}%`, 
                            height: '100%', 
                            background: getEfficiencyColor(campaign.effectiveness_score || 0.75)
                          }}></div>
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                          {((campaign.effectiveness_score || 0.75) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '8px', 
                          background: 'var(--bg-tertiary)', 
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            width: `${campaign.data_completeness_score * 100}%`, 
                            height: '100%', 
                            background: 'var(--primary-500)' 
                          }}></div>
                        </div>
                        <span style={{ fontSize: '0.875rem' }}>
                          {(campaign.data_completeness_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleViewDetails(campaign)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid var(--border-light)',
                            background: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '0.875rem'
                          }}
                          title="View details"
                          data-testid={`view-campaign-${idx}`}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => handleEditCampaign(campaign)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid var(--border-light)',
                            background: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '0.875rem'
                          }}
                          title="Edit campaign"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Info */}
          <div style={{ marginTop: '16px', padding: '16px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Showing {campaigns.length} campaigns
            </p>
            <div style={{ display: 'flex', gap: '8px', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Sort by:</span>
              <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                {sortBy.field.replace('_', ' ')} ({sortBy.order === 'asc' ? 'Ascending' : 'Descending'})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showDetailModal && (
        <CampaignDetailModal
          campaign={selectedCampaign}
          onClose={() => setShowDetailModal(false)}
          onEdit={handleEditCampaign}
        />
      )}

      {showFormModal && (
        <CampaignFormModal
          campaign={editingCampaign}
          onClose={() => setShowFormModal(false)}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
}

export default Campaigns;
